import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Lock, Globe } from 'lucide-react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useToast } from '../../components/ToastProvider';
import SaveButton from '../../components/SaveButton';

export default function ClientSettingsPage() {
  const { user, updateUser } = useClientAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [language, setLanguage] = useState(user?.preferred_language ?? 'es');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Profile is "dirty" when name/language differ from the stored user. After a
  // successful save, updateUser refreshes the context → these match again → the
  // Save button returns to its disabled "Guardado" state automatically.
  const profileDirty = name !== (user?.name ?? '') || language !== (user?.preferred_language ?? 'es');
  const pwValid = newPw.length >= 8 && newPw === confirmPw;

  const saveProfile = async () => {
    setSaving(true);
    try {
      await updateUser({ name, preferred_language: language });
      toast.success(t('portal.settings.profileSaved'));
    } catch {
      toast.error(t('portal.settings.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (newPw.length < 8) { toast.error(t('portal.settings.minChars')); return; }
    if (newPw !== confirmPw) { toast.error(t('portal.settings.noMatch')); return; }
    setSavingPw(true);
    try {
      await updateUser({ password: newPw });
      toast.success(t('portal.settings.passwordSaved'));
      setNewPw('');
      setConfirmPw('');
    } catch {
      toast.error(t('portal.settings.errorSaving'));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
        <Settings className="text-slate-500" size={24} />
        {t('portal.settings.title')}
      </h1>

      {/* Profile */}
      <form onSubmit={e => { e.preventDefault(); void saveProfile(); }} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-800">{t('portal.settings.profile')}</h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.settings.nameLabel')}</label>
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.settings.emailLabel')}</label>
          <input
            type="email" value={user?.email ?? ''} disabled
            className="w-full px-3 py-2 border border-slate-100 rounded-lg text-sm bg-slate-50 text-slate-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            <Globe size={14} className="inline mr-1" />
            {t('portal.settings.languageLabel')}
          </label>
          <select
            value={language} onChange={e => setLanguage(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value="es">{t('portal.languages.es')}</option>
            <option value="en">{t('portal.languages.en')}</option>
            <option value="pl">{t('portal.languages.pl')}</option>
            <option value="it">{t('portal.languages.it')}</option>
          </select>
        </div>
        <SaveButton
          onClick={saveProfile}
          saving={saving}
          isDirty={profileDirty}
          label={t('portal.settings.save')}
          savedLabel={t('common.saved', 'Guardado')}
        />
      </form>

      {/* Password */}
      <form onSubmit={e => { e.preventDefault(); void savePassword(); }} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Lock size={18} />
          {user?.has_password ? t('portal.settings.changePassword') : t('portal.settings.setPassword')}
        </h2>
        <p className="text-sm text-slate-500">
          {user?.has_password
            ? t('portal.settings.changePasswordDesc')
            : t('portal.settings.setPasswordDesc')}
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.settings.newPassword')}</label>
          <input
            type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            minLength={8}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.settings.confirmPassword')}</label>
          <input
            type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <button
          type="submit" disabled={savingPw || !pwValid}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-default transition-colors"
        >
          <Lock size={16} />
          {savingPw ? t('portal.settings.savingPw', 'Guardando…') : user?.has_password ? t('portal.settings.changeBtn') : t('portal.settings.setBtn')}
        </button>
      </form>
    </div>
  );
}
